import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { URLS, Url } from '@wix/da-url';
import { PapiGallection, PapiPremiumFolderData } from '@wix/da-types-papi';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import EyeDisc2 from '@wix/da-shared-react/pkg/Icons/EyeDisc2';
import PremiumFolderDisc from '@wix/da-shared-react/pkg/Icons/PremiumFolderDisc';
import Button from '@wix/da-shared-react/pkg/Button';
import WatchButton from '@wix/da-shared-react/pkg/Button/ProfileOwnerWatchButton';
import CoreIconSmall from '@wix/da-shared-react/pkg/Icons/CoreIconSmall';
import { MobileContext } from '@wix/da-shared-react/pkg/Context';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import { BIEventData } from '@wix/da-shared-react/pkg/biLogger';
import FolderPaywallLayout from './FolderPaywallLayout';

import s from './FolderPaywall.scss';

export interface Props {
  folder: PapiGallection;
  premiumFolderData: PapiPremiumFolderData;
  biData?: BIEventData;
  isCurrentUserProfileOwner: boolean;
  isViewingAsVisitor?: boolean;
}

const FolderPaywall: React.FC<Props> = ({
  folder,
  biData,
  premiumFolderData,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const {
    formattedDollarPrice,
    isPaidFolder,
    isWatchToAccess,
    isPrivilegedFolder,
    subproductId,
  } = normalizePremiumFolderData(folder.premiumFolderData);
  const contentOwner = folder.owner;

  if (isPaidFolder) {
    return (
      <FolderPaywallLayout
        premiumFolderData={premiumFolderData}
        folder={folder}
        icon={<PremiumFolderDisc size={IconSize.MEDIUM} />}
        button={
          <Button
            view="b18"
            fullWidth={isMobile}
            href={subproductId ? Url.checkoutLink(subproductId) : undefined}
            biData={biData}
          >
            <span className={s['buttonLabel']}>
              {t('paywall.premium.folder.unlockFor')} {formattedDollarPrice}
            </span>
          </Button>
        }
      />
    );
  }

  if (isWatchToAccess) {
    return (
      <FolderPaywallLayout
        premiumFolderData={premiumFolderData}
        folder={folder}
        icon={<EyeDisc2 size={IconSize.MEDIUM} />}
        button={
          <WatchButton
            view="b1"
            user={contentOwner!}
            fullWidth={isMobile}
            onToggleWatchSuccess={() => {
              window.location.href = premiumFolderData.galleryUrl!;
            }}
          />
        }
      />
    );
  }

  if (isPrivilegedFolder) {
    return (
      <FolderPaywallLayout
        premiumFolderData={premiumFolderData}
        folder={folder}
        icon={<CoreIconSmall />}
        button={
          <Button view="b18" fullWidth={false} href={URLS.CORE_MEMBERSHIP}>
            <span className={s['buttonLabel']}>
              {t('paywall.privileged.folder.buyCore')}
            </span>
          </Button>
        }
      />
    );
  }

  return null;
};

FolderPaywall.displayName = 'FolderPaywall';

export default FolderPaywall;
